import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useRef, useState, useEffect } from "react";
import { observer, Observer } from "mobx-react";
import GlobalStyles from "../../../Resources/Styles";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { Feather, Ionicons, MaterialIcons } from "@expo/vector-icons";
import { useScreenDimensions, useDevice } from "../../_layout";
import CustomAlert from "../../Components/CustomAlert";
import { useTranslation } from "react-i18next";
import IconEmptyView from "../../Components/IconEmptyView";
import SearchBar from "../../Components/SearchBar";
import PredefinedExercise from "../../Firestore/PredefinedExercise";
import AddPredefinedExerciseView from "../../Exercise/AddPredefinedExerciseView";
import { useDrag, useDrop } from "react-dnd";
import { runInAction } from "mobx";
import FloatingExerciseLink from "../../Components/FloatingExerciseLink";

const ExerciseLibrary = observer(({ coach }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  useEffect(() => {
    coach.setProperty("searchTermPred", "");
    return () => {
      coach.setProperty("searchTermPred", "");
    };
  }, []);

  const ExerciseLinkButton = ({ exercise }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <FloatingExerciseLink
        exercise={exercise}
        onSave={() => {
          coach.updateCoachTransactionExercises(exercise.id);
          coach.startOrResetBatchTimer();
        }}
        onClose={() => {
          setIsHovered(false);
        }}
      >
        <View
          style={{
            opacity: isHovered ? 0.5 : 1,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Observer>
            {() => (
              <Feather
                name="link"
                size={13}
                color={exercise.link ? Colors.bubbleColor : Colors.grayColor}
              />
            )}
          </Observer>
        </View>
      </FloatingExerciseLink>
    );
  };

  const PredefinedExerciseLayoutView = observer(({}) => {
    const [showingDeletePredefinedsAlert, setShowingDeletePredefinedsAlert] =
      useState(false);
    const [predefinedToDelete, setPredefinedToDelete] = useState("");
    const deletePredefinedsAlertTitle =
      "deletePredefinedExerciseAlertTitle-string";
    const deletePredefinedsAlertText =
      "deletePredefinedExerciseAlertText-string";
    const [showingAddPredefinedExercise, setShowingAddPredefinedExercise] =
      useState(false);
    const [selectedPredefinedType, setSelectedPredefinedType] = useState("");

    const canDrag = !coach.searchTermPred;

    const getFilteredPredefinedForTypes = (types) => {
      const searchTermLower = coach.searchTermPred
        ? coach.searchTermPred.toLowerCase()
        : "";

      const predefinedArray = coach.customPredefinedExercises || [];

      if (coach.searchTermPred && predefinedArray.length > 0) {
        return predefinedArray.filter((pred) => {
          return (
            t(pred.name).toLowerCase().includes(searchTermLower) &&
            types.includes(pred.type)
          );
        });
      }

      return predefinedArray.filter((pred) => types.includes(pred.type));
    };

    // Function to move exercises within the same type
    const movePredefinedExercise = (
      dragIndex,
      hoverIndex,
      filteredExercises
    ) => {
      runInAction(() => {
        if (dragIndex === hoverIndex) return;

        // Get the dragged exercise and the target exercise
        const draggedExercise = filteredExercises[dragIndex];
        const targetExercise = filteredExercises[hoverIndex];

        if (!draggedExercise || !targetExercise) return;

        // Store original order values before making changes
        const originalOrders = new Map();
        filteredExercises.forEach((exercise, index) => {
          originalOrders.set(exercise.id, exercise.order);
        });

        // Create a new array with the moved exercise
        const newFiltered = [...filteredExercises];
        newFiltered.splice(dragIndex, 1);
        newFiltered.splice(hoverIndex, 0, draggedExercise);

        // Update the order property for all exercises to be consecutive (0, 1, 2, 3...)
        // This ensures no gaps even if the original order values had gaps
        newFiltered.forEach((exercise, index) => {
          const originalIndex = coach.customPredefinedExercises.findIndex(
            (e) => e.id === exercise.id
          );
          if (originalIndex !== -1) {
            const newOrder = index;
            const originalOrder = originalOrders.get(exercise.id);

            // Update the order
            coach.customPredefinedExercises[originalIndex].order = newOrder;

            // Only add to transaction if the order actually changed
            if (originalOrder !== newOrder) {
              coach.updateCoachTransactionExercises(exercise.id);
            }
          }
        });
      });
    };

    const PredefinedExerciseTypeLayoutView = observer(({ type, types }) => {
      const resolvedTypes = types && types.length > 0 ? types : [type];
      const dndKey = type;

      const filtered = getFilteredPredefinedForTypes(resolvedTypes).sort(
        (a, b) => a.order - b.order
      );

      const PredefinedExerciseButton = observer(
        ({ predefinedExercise, index }) => {
          const [isHovered, setIsHovered] = useState(false);
          const [isDeleteHovered, setIsDeleteHovered] = useState(false);
          const ref = useRef(null);

          // Drag and Drop setup
          const [{ isOver }, drop] = useDrop({
            accept: `predefined-${dndKey}`,
            drop: (item, monitor) => {
              if (item.index !== index) {
                movePredefinedExercise(item.index, index, filtered);
                coach.startOrResetBatchTimer();
              }
            },
            collect: (monitor) => ({
              isOver: monitor.isOver(),
            }),
          });

          const [{ isDragging }, drag, dragPreview] = useDrag({
            canDrag: () => canDrag,
            type: `predefined-${dndKey}`,
            item: {
              type: `predefined-${dndKey}`,
              id: predefinedExercise.id,
              index,
            },
            end: () => {
              coach.startOrResetBatchTimer();
            },
            collect: (monitor) => ({
              isDragging: monitor.isDragging(),
            }),
          });

          drag(drop(ref));

          return (
            <TouchableOpacity
              ref={ref}
              style={{ maxWidth: "100%", alignSelf: "flex-start" }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <View
                style={[
                  styles.AttributeButton,
                  {
                    backgroundColor: isDragging
                      ? Colors.bubbleColor
                      : Colors.whiteColor,
                    borderColor: isHovered
                      ? Colors.bubbleColor
                      : Colors.borderColor,
                    paddingVertical: 8,
                    paddingHorizontal: 6,
                    minHeight: 33,
                    opacity: isDragging ? 0 : isOver ? 0.4 : 1,
                  },
                ]}
              >
                <View
                  style={[
                    GlobalStyles.HStack,
                    {
                      alignItems: "center",
                      gap: 6,
                      maxWidth: "100%",
                      flexShrink: 1,
                    },
                  ]}
                >
                  <MaterialIcons
                    name="drag-indicator"
                    size={12}
                    color={isHovered ? Colors.grayColor : Colors.grayColor}
                    style={{
                      opacity: (isHovered || isMobile) && canDrag ? 1 : 0,
                    }}
                  />

                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      key={index}
                      style={{
                        fontSize: 12,
                        color: isHovered
                          ? Colors.blackColor
                          : Colors.blackColor,
                        flexShrink: 1,
                        flexWrap: "wrap",
                        // RN-web only: helps very long tokens wrap instead of overflowing
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {t(predefinedExercise.name)}
                    </Text>
                  </View>

                  <View
                    style={[
                      GlobalStyles.HStack,
                      { alignItems: "center", gap: 6, flexShrink: 0 },
                    ]}
                  >
                    <ExerciseLinkButton exercise={predefinedExercise} />
                    <TouchableOpacity
                      style={{
                        opacity:
                          isHovered || isMobile
                            ? isDeleteHovered
                              ? 0.6
                              : 1
                            : 0,
                      }}
                      onPress={() => {
                        setPredefinedToDelete(predefinedExercise.id);
                        setShowingDeletePredefinedsAlert(true);
                      }}
                      onMouseEnter={() => !isMobile && setIsDeleteHovered(true)}
                      onMouseLeave={() => setIsDeleteHovered(false)}
                    >
                      <Feather
                        name="x"
                        size={14}
                        color={isHovered ? Colors.redColor : Colors.redColor}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          );
        }
      );

      const AddPredefinedExerciseButton = ({ title }) => {
        const [isHovered, setIsHovered] = useState(false);
        const action = () => {
          if (!coach.isOffline) {
            setSelectedPredefinedType(title);
            setShowingAddPredefinedExercise(true);
          }
        };
        return (
          <TouchableOpacity
            onPress={action}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                styles.AttributeButton,
                {
                  backgroundColor: Colors.whiteColor,
                  borderColor: isHovered
                    ? Colors.bubbleColor
                    : Colors.borderColor,
                  paddingHorizontal: 12,
                  opacity: 1,
                  height: 33,
                  alignItems: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text
                style={{
                  fontSize: 14,
                  color: Colors.grayColor,
                }}
              >
                +
              </Text>
            </View>
          </TouchableOpacity>
        );
      };

      return (
        (coach.searchTermPred === "" || (filtered && filtered.length > 0)) && (
          <View style={{}}>
            <View style={[GlobalStyles.HStack, {}]}>
              <Text style={Fonts.bold14}>{t(type)}</Text>
            </View>

            <View style={{ ...styles.AttributeFlex, gap: 6 }}>
              {filtered.map((predefinedExercise, index) => (
                <PredefinedExerciseButton
                  predefinedExercise={predefinedExercise}
                  index={index}
                  key={predefinedExercise.id}
                />
              ))}
              <AddPredefinedExerciseButton title={type} />
            </View>
          </View>
        )
      );
    });

    if (!coach.hasMatchingExercises()) {
      return (
        <IconEmptyView
          iconLibrary={"MaterialCommunityIcons"}
          mainText={"emptyExercisesTitle-string"}
          subText={"noExercisesFoundText-string"}
          iconName={"magnify-plus-outline"}
          marginTop={140}
          marginBottom={16}
        />
      );
    }

    return (
      <View
        style={{
          flex: 1,
          gap: 16,
        }}
      >
        <>
          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.kneeDominant}
          />

          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.hipDominant}
          />
          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.horizontalPush}
          />
          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.verticalPush}
          />
          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.horizontalPull}
          />
          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.verticalPull}
          />
          <PredefinedExerciseTypeLayoutView
            type={PredefinedExercise.ExerciseTypes.other}
            types={[
              PredefinedExercise.ExerciseTypes.other,
              PredefinedExercise.ExerciseTypes.weightlifting,
            ]}
          />
        </>
        {showingDeletePredefinedsAlert && (
          <CustomAlert
            isVisible={showingDeletePredefinedsAlert}
            onClose={() => setShowingDeletePredefinedsAlert(false)}
            title={deletePredefinedsAlertTitle}
            text={deletePredefinedsAlertText}
            action={() => setShowingDeletePredefinedsAlert(false)}
            specialCTA={"delete-string"}
            coach={coach}
            predefinedToDelete={predefinedToDelete}
          />
        )}
        {showingAddPredefinedExercise && selectedPredefinedType !== "" && (
          <AddPredefinedExerciseView
            coach={coach}
            isVisible={showingAddPredefinedExercise}
            onClose={() => setShowingAddPredefinedExercise(false)}
            title={selectedPredefinedType}
            setTitle={() => setSelectedPredefinedType("")}
          />
        )}
      </View>
    );
  });

  return (
    <View
      style={[
        styles.container,
        { marginTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      <Observer>
        {() => (
          <>
            <View
              style={[
                GlobalStyles.HStack,
                { marginBottom: 16, maxWidth: "60%" },
              ]}
            >
              <SearchBar
                text={coach.searchTermPred}
                onChangeText={(text) => coach.setSearchTermPred(text)}
                placeholder={t("searchExercise-string")}
              />
            </View>

            <PredefinedExerciseLayoutView />
          </>
        )}
      </Observer>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },

  AttributeFlex: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
    // marginBottom: 16,
  },
  AttributeButton: {
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
});

export default ExerciseLibrary;
